import { MobileDrawer } from '@/app/shell/SideBar/SideBarMobileDrawer';
import { NavList } from '@/app/shell/SideBar/SideBarNavList';
import type { SideBarItem } from '@/app/shell/SideBar/sideBarItem';
import logo from '@/assets/brand/isotype.png';
import { ROUTES } from '@/shared/constants/routes';
import { useTrans } from '@/shared/hooks/useTranslation';
import { cn } from '@/shared/utils';
import { noop } from '@/shared/utils/noop';
import { CustomTooltip } from '@/view/shared/Tooltip';
import { useSideBarViewModel } from '@/viewmodel/shared/sidebar';
import { ChevronLeft, ChevronRight, Home } from 'lucide-react';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface SideBarProps {
  readonly items: SideBarItem[];
  readonly activeItem?: string | undefined;
  readonly onItemClick: (item: SideBarItem) => void;
  readonly isMobileOpen?: boolean;
  readonly onMobileClose?: () => void;
  readonly groupLabels?: Record<string, string> | undefined;
  readonly collapsibleGroups?: readonly string[] | undefined;
  readonly footer?: ReactNode;
}

export default function SideBar({
  items,
  activeItem,
  onItemClick,
  isMobileOpen = false,
  onMobileClose,
  groupLabels,
  collapsibleGroups,
  footer,
}: Readonly<SideBarProps>) {
  const { t } = useTrans();
  const { collapsed, toggleCollapsed, activeGroup, openGroups, toggleGroup } = useSideBarViewModel(
    items,
    activeItem,
  );
  const collapseLabel = t('dataset.sidebar.collapseSidebar');
  const expandLabel = t('dataset.sidebar.expandSidebar');
  const [hoveredLabel, setHoveredLabel] = useState<string | null>(null);

  const collapseButton = (
    <CustomTooltip tip={collapsed ? expandLabel : collapseLabel} placement="top">
      <button
        type="button"
        onClick={toggleCollapsed}
        aria-label={collapsed ? expandLabel : collapseLabel}
        aria-expanded={!collapsed}
        className="grid size-7 cursor-pointer place-items-center rounded-md text-muted-foreground transition-colors hover:bg-surface-secondary hover:text-foreground"
      >
        {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
      </button>
    </CustomTooltip>
  );

  return (
    <>
      <aside
        className={cn(
          'hidden shrink-0 flex-col border-r border-border bg-surface transition-all duration-200 ease-in-out md:flex',
          collapsed ? 'w-16 items-center' : 'w-60',
        )}
      >
        <div
          className={cn(
            'flex h-14 shrink-0 items-center border-b border-border',
            collapsed ? 'justify-center px-3' : 'justify-between px-5',
          )}
        >
          <Link
            to={ROUTES.WORKSPACES}
            aria-label="Go to workspaces"
            className={cn(collapsed && 'grid h-7 place-items-center')}
          >
            <img src={logo} alt="Teramot" className={cn('h-6 w-auto', collapsed && 'max-h-5')} />
          </Link>
          {!collapsed && (
            <Link
              to={ROUTES.WORKSPACES}
              aria-label="Go to workspaces"
              className="grid size-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-surface-secondary hover:text-foreground"
            >
              <Home size={16} />
            </Link>
          )}
        </div>
        <NavList
          items={items}
          activeItem={activeItem}
          onItemClick={onItemClick}
          groupLabels={groupLabels}
          collapsibleGroups={collapsibleGroups}
          openGroups={openGroups}
          onToggleGroup={toggleGroup}
          activeGroup={activeGroup}
          idPrefix="sidebar-desktop"
          collapsed={collapsed}
          onHoverLabel={collapsed ? setHoveredLabel : undefined}
        />
        <div className="mt-auto flex w-full shrink-0 flex-col">
          {!collapsed && footer}
          <div
            className={cn('relative flex px-4 pb-4', collapsed ? 'justify-center' : 'justify-end')}
          >
            {collapsed && hoveredLabel && (
              <div className="pointer-events-none absolute bottom-full left-0 z-50 mb-1 w-max rounded-md border border-border bg-surface px-2 py-1 text-body-sm text-foreground shadow-md">
                {hoveredLabel}
              </div>
            )}
            {collapseButton}
          </div>
        </div>
      </aside>

      <MobileDrawer
        items={items}
        activeItem={activeItem}
        onItemClick={onItemClick}
        isOpen={isMobileOpen}
        onClose={onMobileClose ?? noop}
        groupLabels={groupLabels}
        collapsibleGroups={collapsibleGroups}
        openGroups={openGroups}
        onToggleGroup={toggleGroup}
        activeGroup={activeGroup}
        footer={footer}
      />
    </>
  );
}
